import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useSearch, useLocation } from "wouter";
import { useAppStore } from "@/store";
import { apiFetch } from "@/lib/api-fetch";
import { useToast } from "@/hooks/use-toast";
import { PaywallModal, usePaywallCheck } from "@/components/paywall-modal";
import { useGetVoiceProfile } from "@workspace/api-client-react";
import { Preset, PRESETS } from "@/lib/studio-presets";
import { useVaguenessDetection } from "@/hooks/use-vagueness-detection";
import { useInlineVoiceInput } from "@/hooks/use-inline-voice-input";
import { copyForPlatform, formatForLinkedIn, formatForEmail, formatForSlack, formatForMarkdown } from "@/lib/copy-for-platform";
import {
  SavedPreset, SavedPresetConfig,
  STUDIO_USE_CASE_LABELS, STUDIO_SUB_PERSONA_LABELS,
  STUDIO_PLACEHOLDER_BY_SUB, STUDIO_PLACEHOLDER_BY_USE_CASE,
  USE_CASE_TO_SUGGESTED_CATEGORIES,
} from "@/lib/studio-config";

export const STUDIO_DRAFT_KEY = "studio_last_draft";
const PINNED_KEY = "studio_pinned_presets";

export const PLATFORM_OPTIONS: Array<{ id: string; label: string; fmt: (s: string) => string; desc: string }> = [
  { id: "linkedin", label: "LinkedIn",  fmt: formatForLinkedIn, desc: "Spacing + capped hashtags" },
  { id: "email",    label: "Email",     fmt: formatForEmail,    desc: "Clean paragraphs, no markdown" },
  { id: "slack",    label: "Slack",     fmt: formatForSlack,    desc: "Mrkdwn bold, no hashtags" },
  { id: "markdown", label: "Markdown",  fmt: formatForMarkdown, desc: "Preserve formatting as-is" },
];

export function useStudio() {
  const { toast } = useToast();
  const { sessionId, authToken, incrementPostsGenerated, currentIdea } = useAppStore();
  const { showPaywall, forceShowPaywall, dismissPaywall } = usePaywallCheck();
  const searchString = useSearch();
  const [, setLocation] = useLocation();

  const studioBaseUrl = import.meta.env.BASE_URL.replace(/\/$/, "");

  const transcribeAudioInline = useCallback(async (audioBase64: string) => {
    const res = await apiFetch(`${studioBaseUrl}/api/voice/transcribe`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "x-session-id": sessionId },
      body: JSON.stringify({ audioBase64, sessionId }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({})) as Record<string, unknown>;
      throw new Error((body.details as string) || (body.error as string) || "Transcription failed");
    }
    return res.json() as Promise<{ text: string }>;
  }, [studioBaseUrl, sessionId]);

  const [selectedPreset, setSelectedPreset] = useState<Preset | null>(null);
  const [request, setRequest] = useState(() => currentIdea.trim() || "");
  const [output, setOutput] = useState<string | null>(null);
  const [studioDrafts, setStudioDrafts] = useState<Array<{ content: string; style: string; description: string }> | null>(null);
  const [studioActiveDraftIdx, setStudioActiveDraftIdx] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [refining, setRefining] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [copiedPlatform, setCopiedPlatform] = useState<string | null>(null);
  const [platformCopyOpen, setPlatformCopyOpen] = useState(false);
  const [smartCopyLabel, setSmartCopyLabel] = useState<string | null>(null);
  const [feedbackGiven, setFeedbackGiven] = useState<"up" | "down" | null>(null);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [generationId, setGenerationId] = useState(0);
  const [studioUndoStack, setStudioUndoStack] = useState<string[]>([]);
  const [voiceProfileDetected, setVoiceProfileDetected] = useState<boolean | null>(null);
  const [resumeDraft, setResumeDraft] = useState<{ request: string; format: string | null } | null>(null);
  const [showStickyBar, setShowStickyBar] = useState(false);
  const [shared, setShared] = useState(false);
  const [studioAdvancedOpen, setStudioAdvancedOpen] = useState(false);
  const [advancedEverOpened, setAdvancedEverOpened] = useState(() => {
    try { return localStorage.getItem("studio_advanced_seen") === "1"; } catch { return false; }
  });
  const [pinnedPresets, setPinnedPresets] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem(PINNED_KEY);
      if (stored) return JSON.parse(stored) as string[];
    } catch { /* ignore */ }
    return [];
  });
  const [showFormatPicker, setShowFormatPicker] = useState(false);
  const [studioTone, setStudioTone] = useState<number>(50);
  const [studioPostStyle, setStudioPostStyle] = useState<string | undefined>(undefined);
  const [studioHookStyle, setStudioHookStyle] = useState<string | undefined>(undefined);
  const [studioSpacingStyle, setStudioSpacingStyle] = useState<string | undefined>(undefined);
  const [studioPerspective, setStudioPerspective] = useState<string | undefined>(undefined);
  const [studioHumorLevel, setStudioHumorLevel] = useState<string | undefined>(undefined);
  const [studioAudience, setStudioAudience] = useState<string | undefined>(undefined);
  const [studioEmojiUsage, setStudioEmojiUsage] = useState<string | undefined>(undefined);
  const [studioTargetWordCount, setStudioTargetWordCount] = useState<string>("");
  const [studioCustomInstruction, setStudioCustomInstruction] = useState<string>("");
  const [studioVoiceState, setStudioVoiceState] = useState<string | undefined>(undefined);
  const [studioAudienceDescription, setStudioAudienceDescription] = useState<string>("");
  const [studioBlendStyle, setStudioBlendStyle] = useState<string | undefined>(undefined);
  const [studioRecentTopics, setStudioRecentTopics] = useState<Array<{ topic: string; format: string }>>([]);
  const [studioCoachTip, setStudioCoachTip] = useState<string | null>(null);
  const [studioCoachLoading, setStudioCoachLoading] = useState(false);
  const [studioCoachDismissed, setStudioCoachDismissed] = useState(false);
  const [ghostDraftLoading, setGhostDraftLoading] = useState(false);
  const [copilotHint, setCopilotHint] = useState<string | null>(null);
  const [inferredIntent, setInferredIntent] = useState<string | null>(null);
  const [intentEditing, setIntentEditing] = useState(false);
  const [intentEditValue, setIntentEditValue] = useState("");
  const [remixOpen, setRemixOpen] = useState(false);
  const [remixLoading, setRemixLoading] = useState(false);
  const [remixSourceText, setRemixSourceText] = useState<string | null>(null);
  const [remixSourceFormat, setRemixSourceFormat] = useState<string>("custom");
  const [savedPresets, setSavedPresets] = useState<SavedPreset[]>([]);
  const [activePresetId, setActivePresetId] = useState<string | null>(null);
  const [savePresetInputOpen, setSavePresetInputOpen] = useState(false);
  const [savePresetName, setSavePresetName] = useState("");
  const [savingPreset, setSavingPreset] = useState(false);

  const copilotDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const intentDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const outputRef = useRef<HTMLDivElement>(null);
  const generateBtnRef = useRef<HTMLButtonElement>(null);
  const autoGenerateFiredRef = useRef(false);
  const presetLoadFiredRef = useRef(false);
  const formatLoadFiredRef = useRef(false);

  const { data: studioVoiceProfile } = useGetVoiceProfile({
    request: { headers: { "x-session-id": sessionId } },
  });

  const studioVoiceInput = useInlineVoiceInput({
    onTranscript: (text) => setRequest((prev) => prev ? prev + " " + text : text),
    transcribeAudio: transcribeAudioInline,
  });

  const studioModeInfo = useMemo(() => {
    const prefs = studioVoiceProfile?.preferences as {
      useCaseContext?: string;
      subUseCaseContext?: string | null;
      useCaseContexts?: string[];
      subUseCaseContexts?: string[];
    } | null | undefined;
    const ucs = prefs?.useCaseContexts ?? (prefs?.useCaseContext ? [prefs.useCaseContext] : []);
    const subs = prefs?.subUseCaseContexts ?? (prefs?.subUseCaseContext ? [prefs.subUseCaseContext] : []);
    const uc = ucs[0];
    const sub = subs[0];
    const ucLabel = uc ? STUDIO_USE_CASE_LABELS[uc] : null;
    const subLabel = sub ? STUDIO_SUB_PERSONA_LABELS[sub] : null;
    const multiUcLabel = ucs.length > 1
      ? ucs.map(u => STUDIO_USE_CASE_LABELS[u] ?? u).join(" · ")
      : ucLabel;
    const badgeLabel = subLabel ? `${ucLabel ?? ""} · ${subLabel}` : multiUcLabel;
    const placeholder = (sub && STUDIO_PLACEHOLDER_BY_SUB[sub])
      ?? (uc && STUDIO_PLACEHOLDER_BY_USE_CASE[uc])
      ?? "What should I write? Pick a format above or describe exactly what you need...";
    const suggestedCategories = uc ? (USE_CASE_TO_SUGGESTED_CATEGORIES[uc] ?? []) : [];
    return { badgeLabel, placeholder, suggestedCategories };
  }, [studioVoiceProfile]);

  const studioAdvancedCount = [studioPostStyle, studioHookStyle, studioSpacingStyle, studioPerspective, studioHumorLevel, studioAudience, studioEmojiUsage, studioVoiceState, studioBlendStyle].filter(Boolean).length + (studioTargetWordCount.trim() ? 1 : 0) + (studioCustomInstruction.trim() ? 1 : 0) + (studioAudienceDescription.trim() ? 1 : 0);
  const studioVoiceStates = (studioVoiceProfile?.analysis as Record<string, unknown> | undefined)?.voiceStates as Array<{ label: string; description: string; dominantTone: string; exampleContext: string }> | undefined;

  const clearStudioAdvanced = () => {
    setStudioTone(50); setStudioPostStyle(undefined); setStudioHookStyle(undefined); setStudioSpacingStyle(undefined);
    setStudioPerspective(undefined); setStudioHumorLevel(undefined); setStudioAudience(undefined); setStudioEmojiUsage(undefined);
    setStudioTargetWordCount(""); setStudioCustomInstruction(""); setStudioVoiceState(undefined);
    setStudioAudienceDescription(""); setStudioBlendStyle(undefined);
  };

  const isRequestVague = useVaguenessDetection(request);

  const fetchSavedPresets = useCallback(async () => {
    try {
      const res = await apiFetch(`${studioBaseUrl}/api/studio/presets`, {
        headers: { "x-session-id": sessionId },
      });
      if (res.ok) {
        const data = await res.json() as { presets: SavedPreset[] };
        setSavedPresets(data.presets ?? []);
      }
    } catch { /* non-critical */ }
  }, [sessionId, studioBaseUrl]);

  useEffect(() => { fetchSavedPresets(); }, [fetchSavedPresets]);

  const handleLoadPreset = useCallback((preset: SavedPreset) => {
    const cfg = preset.config;
    setStudioTone(typeof cfg.tone === "number" ? cfg.tone : 50);
    setStudioPostStyle(cfg.postStyle ?? undefined);
    setStudioHookStyle(cfg.hookStyle ?? undefined);
    setStudioSpacingStyle(cfg.spacingStyle ?? undefined);
    setStudioPerspective(cfg.perspective ?? undefined);
    setStudioHumorLevel(cfg.humorLevel ?? undefined);
    setStudioAudience(cfg.audience ?? undefined);
    setStudioEmojiUsage(cfg.emojiUsage ?? undefined);
    setStudioTargetWordCount(cfg.targetWordCount ? String(cfg.targetWordCount) : "");
    setStudioCustomInstruction(cfg.customInstruction ?? "");
    setStudioVoiceState(cfg.voiceState ?? undefined);
    setStudioAudienceDescription(cfg.audienceDescription ?? "");
    setStudioBlendStyle(cfg.blendStyle ?? undefined);
    if (cfg.format) {
      const match = PRESETS.find(p => p.label === cfg.format);
      if (match) setSelectedPreset(match);
    }
    setActivePresetId(preset.id);
    setStudioAdvancedOpen(true);
    toast({ title: `Loaded: ${preset.name}`, description: "Advanced settings applied." });
  }, [toast]);

  const handleSavePreset = useCallback(async () => {
    const name = savePresetName.trim();
    if (!name) return;
    setSavingPreset(true);
    try {
      const parsedWC = parseInt(studioTargetWordCount, 10);
      const config: SavedPresetConfig = {
        ...(selectedPreset?.label ? { format: selectedPreset.label } : {}),
        tone: studioTone,
        ...(studioPostStyle ? { postStyle: studioPostStyle } : {}),
        ...(studioHookStyle ? { hookStyle: studioHookStyle } : {}),
        ...(studioSpacingStyle ? { spacingStyle: studioSpacingStyle } : {}),
        ...(studioPerspective ? { perspective: studioPerspective } : {}),
        ...(studioHumorLevel ? { humorLevel: studioHumorLevel } : {}),
        ...(studioAudience ? { audience: studioAudience } : {}),
        ...(studioEmojiUsage ? { emojiUsage: studioEmojiUsage } : {}),
        ...(!isNaN(parsedWC) && parsedWC >= 5 ? { targetWordCount: parsedWC } : {}),
        ...(studioCustomInstruction.trim() ? { customInstruction: studioCustomInstruction.trim() } : {}),
        ...(studioVoiceState ? { voiceState: studioVoiceState } : {}),
        ...(studioAudienceDescription.trim() ? { audienceDescription: studioAudienceDescription.trim() } : {}),
        ...(studioBlendStyle ? { blendStyle: studioBlendStyle } : {}),
      };
      const res = await apiFetch(`${studioBaseUrl}/api/studio/presets`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-session-id": sessionId },
        body: JSON.stringify({ name, config }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({})) as { error?: string };
        throw new Error(err.error ?? "Failed to save preset");
      }
      const data = await res.json() as { preset: SavedPreset };
      setSavedPresets(prev => [data.preset, ...prev]);
      setActivePresetId(data.preset.id);
      setSavePresetInputOpen(false);
      setSavePresetName("");
      toast({ title: `"${name}" saved`, description: "Load it anytime from My Presets." });
    } catch (err) {
      toast({ title: "Couldn't save preset", description: err instanceof Error ? err.message : "Please try again.", variant: "destructive" });
    } finally {
      setSavingPreset(false);
    }
  }, [savePresetName, sessionId, selectedPreset, studioTone, studioPostStyle, studioHookStyle, studioSpacingStyle, studioPerspective, studioHumorLevel, studioAudience, studioEmojiUsage, studioTargetWordCount, studioCustomInstruction, studioVoiceState, studioAudienceDescription, studioBlendStyle, toast, studioBaseUrl]);

  const handleDeletePreset = useCallback(async (id: string, name: string) => {
    try {
      const res = await apiFetch(`${studioBaseUrl}/api/studio/presets/${id}`, {
        method: "DELETE",
        headers: { "x-session-id": sessionId },
      });
      if (!res.ok) throw new Error("Delete failed");
      setSavedPresets(prev => prev.filter(p => p.id !== id));
      if (activePresetId === id) setActivePresetId(null);
      toast({ title: `"${name}" removed` });
    } catch {
      toast({ title: "Couldn't delete preset", variant: "destructive" });
    }
  }, [sessionId, activePresetId, toast, studioBaseUrl]);

  const recordPresetUse = useCallback(async (id: string) => {
    try {
      await apiFetch(`${studioBaseUrl}/api/studio/presets/${id}/use`, {
        method: "POST",
        headers: { "x-session-id": sessionId },
      });
      setSavedPresets(prev => prev.map(p => p.id === id ? { ...p, usageCount: p.usageCount + 1 } : p));
    } catch { /* non-critical */ }
  }, [sessionId, studioBaseUrl]);

  const handlePresetClick = useCallback((preset: Preset) => {
    if (selectedPreset?.label === preset.label) {
      setSelectedPreset(null);
      setRequest("");
      return;
    }
    setSelectedPreset(preset);
    setRequest(preset.prompt);
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        const len = preset.prompt.length;
        textareaRef.current.setSelectionRange(len, len);
      }
    }, 50);
  }, [selectedPreset]);

  const handleGhostDraft = useCallback(async () => {
    if (!selectedPreset) return;
    setGhostDraftLoading(true);
    setOutput(null);
    setStudioDrafts(null);
    setSaved(false);
    setFeedbackGiven(null);
    try {
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        "x-session-id": sessionId,
      };
      if (authToken) headers["Authorization"] = `Bearer ${authToken}`;
      const res = await apiFetch(`${studioBaseUrl}/api/studio/ghost-draft`, {
        method: "POST",
        headers,
        body: JSON.stringify({ format: selectedPreset.label }),
      });
      if (res.status === 403) {
        const err = await res.json().catch(() => ({}));
        const code = (err as { code?: string }).code;
        if (code === "FREE_LIMIT_REACHED" || code === "SUBSCRIPTION_REQUIRED") { forceShowPaywall(); return; }
      }
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as { error?: string }).error || "Something went wrong. Please try again.");
      }
      const data = await res.json() as { content: string; hasVoice: boolean };
      setVoiceProfileDetected(data.hasVoice);
      setOutput(data.content ?? null);
      setStudioDrafts(null);
      setStudioUndoStack([]);
      setGenerationId(id => id + 1);
      setStudioCoachTip(null);
      setStudioCoachLoading(false);
      setStudioCoachDismissed(false);
      incrementPostsGenerated();
      setTimeout(() => { outputRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }); }, 100);
    } catch (err) {
      toast({ title: "Ghost draft failed", description: err instanceof Error ? err.message : "Please try again.", variant: "destructive" });
    } finally {
      setGhostDraftLoading(false);
    }
  }, [selectedPreset, sessionId, authToken, forceShowPaywall, toast, incrementPostsGenerated, studioBaseUrl]);

  const fetchCopilotHint = useCallback(async (partialPrompt: string) => {
    if (partialPrompt.trim().length < 8) { setCopilotHint(null); return; }
    try {
      const res = await apiFetch(`${studioBaseUrl}/api/studio/copilot-hint`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-session-id": sessionId },
        body: JSON.stringify({ partialPrompt, format: selectedPreset?.label ?? "custom" }),
      });
      if (res.ok) {
        const data = await res.json() as { hint: string | null };
        setCopilotHint(data.hint ?? null);
      }
    } catch { setCopilotHint(null); }
  }, [sessionId, selectedPreset, studioBaseUrl]);

  const fetchInferredIntent = useCallback(async (prompt: string) => {
    if (prompt.trim().length < 8) { setInferredIntent(null); return; }
    try {
      const res = await apiFetch(`${studioBaseUrl}/api/studio/infer-intent`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-session-id": sessionId },
        body: JSON.stringify({ prompt }),
      });
      if (res.ok) {
        const data = await res.json() as { intent: string | null };
        setInferredIntent(data.intent ?? null);
      }
    } catch { setInferredIntent(null); }
  }, [sessionId, studioBaseUrl]);

  const handleRequestChange = useCallback((value: string) => {
    setRequest(value);
    setCopilotHint(null);
    if (copilotDebounceRef.current) clearTimeout(copilotDebounceRef.current);
    if (intentDebounceRef.current) clearTimeout(intentDebounceRef.current);
    if (value.trim().length >= 8) {
      copilotDebounceRef.current = setTimeout(() => { fetchCopilotHint(value); }, 600);
      intentDebounceRef.current = setTimeout(() => { fetchInferredIntent(value); }, 800);
    } else {
      setInferredIntent(null);
    }
  }, [fetchCopilotHint, fetchInferredIntent]);

  const fetchStudioRecentContext = useCallback(async () => {
    try {
      const res = await apiFetch(`${studioBaseUrl}/api/studio/context`, {
        headers: { "x-session-id": sessionId },
      });
      if (res.ok) {
        const data = await res.json() as { recentTopics: Array<{ topic: string; format: string }> };
        setStudioRecentTopics(data.recentTopics ?? []);
      }
    } catch { /* non-critical, ignore */ }
  }, [sessionId, studioBaseUrl]);

  const clearStudioContext = useCallback(async () => {
    try {
      await apiFetch(`${studioBaseUrl}/api/studio/context`, {
        method: "DELETE",
        headers: { "x-session-id": sessionId },
      });
      setStudioRecentTopics([]);
    } catch { /* ignore */ }
  }, [sessionId, studioBaseUrl]);

  const fetchStudioCoachTip = useCallback(async (content: string) => {
    setStudioCoachTip(null);
    setStudioCoachDismissed(false);
    setStudioCoachLoading(true);
    try {
      const res = await apiFetch(`${studioBaseUrl}/api/posts/coach`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-session-id": sessionId },
        body: JSON.stringify({ content }),
      });
      if (res.ok) {
        const data = await res.json() as { tip: string | null };
        setStudioCoachTip(data.tip ?? null);
      }
    } catch {
    } finally {
      setStudioCoachLoading(false);
    }
  }, [sessionId, studioBaseUrl]);

  const handleGenerate = useCallback(async () => {
    const trimmed = request.trim();
    if (!trimmed || trimmed.length < 5) {
      toast({ title: "Tell me what to create", description: "Pick a preset or type what you want.", variant: "destructive" });
      return;
    }
    setIsGenerating(true);
    setOutput(null);
    setStudioDrafts(null);
    setSaved(false);
    setFeedbackGiven(null);
    try {
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        "x-session-id": sessionId,
      };
      if (authToken) headers["Authorization"] = `Bearer ${authToken}`;
      const parsedWC = parseInt(studioTargetWordCount, 10);
      const wordCountParam = !isNaN(parsedWC) && parsedWC >= 5 ? parsedWC : undefined;
      const intentInstruction = inferredIntent
        ? `${studioCustomInstruction.trim() ? studioCustomInstruction.trim() + " " : ""}Goal: ${inferredIntent}`
        : studioCustomInstruction.trim() || undefined;
      const res = await apiFetch(`${studioBaseUrl}/api/studio/create`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          request: trimmed, format: selectedPreset?.label ?? "custom", tone: studioTone,
          postStyle: studioPostStyle, hookStyle: studioHookStyle, spacingStyle: studioSpacingStyle,
          perspective: studioPerspective, humorLevel: studioHumorLevel, audience: studioAudience,
          emojiUsage: studioEmojiUsage, targetWordCount: wordCountParam, customInstruction: intentInstruction,
          voiceState: studioVoiceState, audienceDescription: studioAudienceDescription.trim() || undefined,
          blendStyle: studioBlendStyle, count: 4,
        }),
      });
      if (res.status === 403) {
        const err = await res.json().catch(() => ({}));
        const code = (err as { code?: string }).code;
        if (code === "FREE_LIMIT_REACHED" || code === "SUBSCRIPTION_REQUIRED") { forceShowPaywall(); return; }
      }
      if (res.status === 429) {
        toast({ title: "Slow down — you're generating too fast. Try again in a moment.", variant: "destructive" });
        return;
      }
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as { error?: string }).error || "Something went wrong. Please try again.");
      }
      const data = await res.json() as { content?: string; drafts?: Array<{ content: string; style: string; description: string }>; hasVoice: boolean };
      setVoiceProfileDetected(data.hasVoice);
      setStudioCoachTip(null);
      setStudioCoachLoading(false);
      setStudioCoachDismissed(false);
      if (data.drafts && data.drafts.length > 0) {
        setStudioDrafts(data.drafts.map((d) => ({ content: d.content, style: d.style ?? "", description: d.description ?? "" })));
        setStudioActiveDraftIdx(0);
        setOutput(null);
      } else {
        setOutput(data.content ?? null);
        setStudioDrafts(null);
      }
      setStudioUndoStack([]);
      setGenerationId(id => id + 1);
      incrementPostsGenerated();
      setResumeDraft(null);
      try { localStorage.removeItem(STUDIO_DRAFT_KEY); } catch { /* ignore */ }
      if (activePresetId) recordPresetUse(activePresetId);
      setTimeout(() => { outputRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }); }, 100);
    } catch (err) {
      toast({ title: "Generation failed", description: err instanceof Error ? err.message : "Please try again.", variant: "destructive" });
    } finally {
      setIsGenerating(false);
    }
  }, [request, sessionId, authToken, selectedPreset, forceShowPaywall, toast, incrementPostsGenerated, studioTone, studioPostStyle, studioHookStyle, studioSpacingStyle, studioPerspective, studioHumorLevel, studioAudience, studioEmojiUsage, studioTargetWordCount, studioCustomInstruction, studioVoiceState, inferredIntent, studioAudienceDescription, studioBlendStyle, activePresetId, recordPresetUse, studioBaseUrl]);

  const handleRemix = useCallback(async (targetFormat: string) => {
    const draftText = remixSourceText;
    if (!draftText) return;
    setRemixLoading(true);
    try {
      const headers: Record<string, string> = { "Content-Type": "application/json", "x-session-id": sessionId };
      if (authToken) headers["Authorization"] = `Bearer ${authToken}`;
      const res = await apiFetch(`${studioBaseUrl}/api/studio/remix`, {
        method: "POST",
        headers,
        body: JSON.stringify({ draftText, sourceFormat: remixSourceFormat, targetFormat }),
      });
      if (res.status === 403) {
        const err = await res.json().catch(() => ({}));
        const code = (err as { code?: string }).code;
        if (code === "FREE_LIMIT_REACHED" || code === "SUBSCRIPTION_REQUIRED") { forceShowPaywall(); return; }
      }
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as { error?: string }).error || "Remix failed.");
      }
      const data = await res.json() as { content: string; hasVoice: boolean };
      setOutput(data.content);
      setStudioDrafts(null);
      setStudioUndoStack([]);
      setGenerationId(id => id + 1);
      setSaved(false);
      setRemixOpen(false);
      setRemixSourceText(null);
      toast({ title: "Remixed!", description: `Converted to ${targetFormat}.` });
      setTimeout(() => { outputRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }); }, 100);
    } catch (err) {
      toast({ title: "Remix failed", description: err instanceof Error ? err.message : "Please try again.", variant: "destructive" });
    } finally {
      setRemixLoading(false);
    }
  }, [remixSourceText, remixSourceFormat, sessionId, authToken, forceShowPaywall, toast, studioBaseUrl]);

  const handleShare = useCallback(async () => {
    if (!output) return;
    if (navigator.share) {
      try { await navigator.share({ text: output }); } catch { /* user dismissed */ }
    } else {
      try { await navigator.clipboard.writeText(output); } catch { /* ignore */ }
      setShared(true);
      setTimeout(() => setShared(false), 1500);
    }
  }, [output]);

  const handleResumeDraft = useCallback(() => {
    if (!resumeDraft) return;
    setRequest(resumeDraft.request);
    if (resumeDraft.format) {
      const preset = PRESETS.find(p => p.label === resumeDraft.format);
      if (preset) setSelectedPreset(preset);
    }
    setResumeDraft(null);
  }, [resumeDraft]);

  const handleRefine = async (refinementKey: string) => {
    if (!output || refining) return;
    const previousOutput = output;
    setStudioUndoStack(prev => [previousOutput, ...prev].slice(0, 5));
    setRefining(refinementKey);
    try {
      const headers: Record<string, string> = { "Content-Type": "application/json", "x-session-id": sessionId };
      if (authToken) headers["Authorization"] = `Bearer ${authToken}`;
      const res = await apiFetch(`${studioBaseUrl}/api/studio/refine`, {
        method: "POST",
        headers,
        body: JSON.stringify({ originalRequest: request.trim(), originalContent: output, refinement: refinementKey, format: selectedPreset?.label ?? "custom" }),
      });
      if (res.status === 403) {
        const err = await res.json().catch(() => ({}));
        const code = (err as { code?: string }).code;
        if (code === "FREE_LIMIT_REACHED" || code === "SUBSCRIPTION_REQUIRED") { setStudioUndoStack(prev => prev.slice(1)); forceShowPaywall(); return; }
      }
      if (res.status === 429) {
        setStudioUndoStack(prev => prev.slice(1));
        toast({ title: "Slow down — you're generating too fast. Try again in a moment.", variant: "destructive" });
        return;
      }
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as { error?: string }).error || "Something went wrong.");
      }
      const data = await res.json() as { content: string };
      setOutput(data.content);
      setGenerationId(id => id + 1);
      setSaved(false);
    } catch (err) {
      setStudioUndoStack(prev => prev.slice(1));
      toast({ title: "Refinement failed", description: err instanceof Error ? err.message : "Please try again.", variant: "destructive" });
    } finally {
      setRefining(null);
    }
  };

  const handleStudioUndo = () => {
    if (!studioUndoStack.length) return;
    setOutput(studioUndoStack[0]!);
    setStudioUndoStack(prev => prev.slice(1));
    setGenerationId(id => id + 1);
  };

  const getSmartCopyPlatform = useCallback((): { id: string; label: string; fmt: (s: string) => string } | null => {
    const presetLabel = selectedPreset?.label?.toLowerCase() ?? "";
    if (presetLabel.includes("linkedin")) return PLATFORM_OPTIONS[0]!;
    if (presetLabel.includes("email") || presetLabel.includes("newsletter") || presetLabel.includes("cold email") || presetLabel.includes("follow-up") || presetLabel.includes("welcome email")) return PLATFORM_OPTIONS[1]!;
    if (presetLabel.includes("slack")) return PLATFORM_OPTIONS[2]!;
    return null;
  }, [selectedPreset]);

  const handleCopy = useCallback(async () => {
    if (!output) return;
    const platform = getSmartCopyPlatform();
    const text = platform ? platform.fmt(output) : output;
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setSmartCopyLabel(platform ? `Formatted for ${platform.label} ✓` : null);
    setTimeout(() => { setCopied(false); setSmartCopyLabel(null); }, 1800);
  }, [output, getSmartCopyPlatform]);

  const handleCopyForPlatform = async (id: string, fmt: (s: string) => string) => {
    if (!output) return;
    await navigator.clipboard.writeText(fmt(output));
    setCopiedPlatform(id);
    setTimeout(() => { setCopiedPlatform(null); setPlatformCopyOpen(false); }, 1500);
  };

  const handleRegenerate = () => {
    setOutput(null);
    setSaved(false);
    setFeedbackGiven(null);
    handleGenerate();
  };

  const handleFeedback = async (signal: "up" | "down", draftStyle?: string) => {
    if (!output || feedbackGiven || feedbackLoading) return;
    setFeedbackLoading(true);
    try {
      await apiFetch(`${studioBaseUrl}/api/studio/feedback`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-session-id": sessionId },
        body: JSON.stringify({ postContent: output, signal, ...(draftStyle ? { draftStyle } : {}) }),
      });
      setFeedbackGiven(signal);
      if (signal === "down") {
        toast({ title: "Got it — won't repeat that style", description: "Future outputs will avoid this structure and tone." });
      } else {
        toast({ title: "Sounds like you!", description: "Positive signals like this improve future generations." });
      }
    } catch { /* non-critical */ } finally {
      setFeedbackLoading(false);
    }
  };

  const handleFeedbackSilent = async (content: string, signal: "up" | "down", draftStyle?: string) => {
    try {
      await apiFetch(`${studioBaseUrl}/api/studio/feedback`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-session-id": sessionId },
        body: JSON.stringify({ postContent: content, signal, ...(draftStyle ? { draftStyle } : {}) }),
      });
    } catch { /* non-critical */ }
  };

  const handleSaveToVault = async () => {
    if (!output || isSaving || saved) return;
    setIsSaving(true);
    try {
      const headers: Record<string, string> = { "Content-Type": "application/json", "x-session-id": sessionId };
      if (authToken) headers["Authorization"] = `Bearer ${authToken}`;
      const title = selectedPreset?.label
        ? `${selectedPreset.label} — ${request.replace(selectedPreset.prompt, "").trim().slice(0, 60) || "Studio"}`
        : request.trim().slice(0, 80) || "Studio creation";
      const res = await apiFetch(`${studioBaseUrl}/api/ideas`, {
        method: "POST",
        headers,
        body: JSON.stringify({ title, notes: output, source: "studio" }),
      });
      if (!res.ok) throw new Error("Failed to save");
      setSaved(true);
      toast({ title: "Saved to Vault", description: "You can find it in your Idea Vault." });
    } catch {
      toast({ title: "Couldn't save", description: "Please try again.", variant: "destructive" });
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    if (output && generationId > 0) fetchStudioCoachTip(output);
  }, [generationId]);

  useEffect(() => {
    if (studioAdvancedOpen) fetchStudioRecentContext();
  }, [studioAdvancedOpen, fetchStudioRecentContext]);

  useEffect(() => {
    if (autoGenerateFiredRef.current) return;
    const params = new URLSearchParams(searchString);
    if (params.get("autoGenerate") === "1" && request.trim().length >= 5) {
      autoGenerateFiredRef.current = true;
      const t = setTimeout(() => { handleGenerate(); }, 150);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [searchString, request, handleGenerate]);

  useEffect(() => {
    if (presetLoadFiredRef.current) return;
    const params = new URLSearchParams(searchString);
    const presetId = params.get("preset");
    if (!presetId || savedPresets.length === 0) return;
    const preset = savedPresets.find(p => p.id === presetId);
    if (preset) { presetLoadFiredRef.current = true; handleLoadPreset(preset); }
  }, [searchString, savedPresets, handleLoadPreset]);

  useEffect(() => {
    if (formatLoadFiredRef.current) return;
    const params = new URLSearchParams(searchString);
    const formatLabel = params.get("format");
    if (!formatLabel) return;
    const preset = PRESETS.find(p => p.label.toLowerCase() === formatLabel.toLowerCase());
    if (preset) { formatLoadFiredRef.current = true; handlePresetClick(preset); }
  }, [searchString, handlePresetClick]);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(STUDIO_DRAFT_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as { v: number; request: string; format: string | null };
        if (parsed.v === 1 && parsed.request && !request.trim()) {
          setResumeDraft({ request: parsed.request, format: parsed.format });
        }
      }
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (!request.trim() && !selectedPreset) return;
    const t = setTimeout(() => {
      try {
        localStorage.setItem(STUDIO_DRAFT_KEY, JSON.stringify({ v: 1, request: request.trim(), format: selectedPreset?.label ?? null }));
      } catch { /* ignore */ }
    }, 500);
    return () => clearTimeout(t);
  }, [request, selectedPreset]);

  useEffect(() => {
    const btn = generateBtnRef.current;
    if (!btn) return;
    const observer = new IntersectionObserver(
      ([entry]) => { setShowStickyBar(!(entry?.isIntersecting ?? true)); },
      { threshold: 0.1 }
    );
    observer.observe(btn);
    return () => observer.disconnect();
  }, []);

  return {
    sessionId, setLocation,
    showPaywall, dismissPaywall,
    selectedPreset, setSelectedPreset,
    request, setRequest, handleRequestChange,
    output, setOutput,
    studioDrafts, setStudioDrafts,
    studioActiveDraftIdx, setStudioActiveDraftIdx,
    isGenerating, refining,
    copied, setCopied,
    copiedPlatform, platformCopyOpen, setPlatformCopyOpen,
    smartCopyLabel, shared, setShared,
    feedbackGiven, setFeedbackGiven,
    feedbackLoading, isSaving, saved, setSaved,
    generationId,
    studioUndoStack, setStudioUndoStack,
    voiceProfileDetected,
    resumeDraft, setResumeDraft,
    showStickyBar,
    studioAdvancedOpen, setStudioAdvancedOpen,
    advancedEverOpened, setAdvancedEverOpened,
    pinnedPresets, setPinnedPresets,
    showFormatPicker, setShowFormatPicker,
    studioTone, setStudioTone,
    studioPostStyle, setStudioPostStyle,
    studioHookStyle, setStudioHookStyle,
    studioSpacingStyle, setStudioSpacingStyle,
    studioPerspective, setStudioPerspective,
    studioHumorLevel, setStudioHumorLevel,
    studioAudience, setStudioAudience,
    studioEmojiUsage, setStudioEmojiUsage,
    studioTargetWordCount, setStudioTargetWordCount,
    studioCustomInstruction, setStudioCustomInstruction,
    studioVoiceState, setStudioVoiceState,
    studioAudienceDescription, setStudioAudienceDescription,
    studioBlendStyle, setStudioBlendStyle,
    studioRecentTopics, clearStudioContext,
    studioCoachTip, studioCoachLoading, studioCoachDismissed, setStudioCoachDismissed,
    ghostDraftLoading,
    copilotHint, setCopilotHint,
    inferredIntent, setInferredIntent,
    intentEditing, setIntentEditing,
    intentEditValue, setIntentEditValue,
    remixOpen, setRemixOpen,
    remixLoading,
    remixSourceText, setRemixSourceText,
    remixSourceFormat, setRemixSourceFormat,
    savedPresets, activePresetId,
    savePresetInputOpen, setSavePresetInputOpen,
    savePresetName, setSavePresetName,
    savingPreset,
    studioAdvancedCount, studioVoiceStates,
    clearStudioAdvanced,
    isRequestVague,
    studioVoiceInput,
    studioModeInfo,
    textareaRef, outputRef, generateBtnRef,
    handlePresetClick, handleLoadPreset, handleDeletePreset,
    handleSavePreset, handleGhostDraft,
    handleGenerate, handleRemix, handleRefine,
    handleStudioUndo, handleShare, handleResumeDraft,
    handleCopy, handleCopyForPlatform,
    handleRegenerate, handleFeedback, handleFeedbackSilent,
    handleSaveToVault,
    fetchStudioCoachTip,
    PINNED_KEY,
  };
}
